import {createAsyncThunk} from '@reduxjs/toolkit';
import type {RootState} from '@/store';
import type {FolderData} from './components/folders-list/folders-list.types';
import type {DesignData} from './components/design-grid/design-grid.types';

interface FetchFoldersArg {
  viewKey: string;
  folderId?: string;
  isShared?: boolean;
}

interface FetchFoldersResult {
  viewKey: string;
  folders: FolderData[];
}

interface FetchDesignsArg {
  viewKey: string;
  folderId?: string;
  isShared?: boolean;
}

interface FetchDesignsResult {
  viewKey: string;
  designs: DesignData[];
  more: boolean;
  nextOffset: number;
}

export const fetchFolders = createAsyncThunk<FetchFoldersResult, FetchFoldersArg, {state: RootState}>(
  'posterDesignPanel/fetchFolders',
  async ({viewKey, isShared}) => {
    const response = (await window.PMW.writeLocal('mydesigns/getFolders', {isShared: isShared ?? false})) as {folders: FolderData[]};
    return {viewKey, folders: response.folders};
  },
  {
    condition: ({viewKey, folderId}, {getState}) => {
      // Inside a folder, folder contents are loaded by fetchFolderContents — don't double-fetch.
      if (folderId !== undefined) return false;
      const view = getState().posterDesignPanel.views[viewKey];
      if (view?.foldersLoading) return false;
      return !view?.foldersLoaded;
    },
  },
);

export const fetchDesigns = createAsyncThunk<FetchDesignsResult, FetchDesignsArg, {state: RootState}>(
  'posterDesignPanel/fetchDesigns',
  async ({viewKey, folderId, isShared}, {getState}) => {
    const offset = getState().posterDesignPanel.views[viewKey]?.designsOffset ?? 0;

    if (folderId !== undefined) {
      const response = (await window.PMW.writeLocal('mydesigns/getFolderContents', {
        folderId,
        offset,
        isShared: isShared ?? false,
      })) as {designs: DesignData[]; more: boolean};
      return {viewKey, designs: response.designs, more: response.more, nextOffset: offset + response.designs.length};
    }

    const response = (await window.PMW.writeLocal('mydesigns/getDesigns', {offset, isShared: isShared ?? false})) as {designs: DesignData[]; more: boolean};
    return {viewKey, designs: response.designs, more: response.more, nextOffset: offset + response.designs.length};
  },
  {
    condition: ({viewKey, folderId}, {getState}) => {
      const view = getState().posterDesignPanel.views[viewKey];
      if (view?.designsLoading) return false;
      // Inside a folder, the initial designs page is loaded by fetchFolderContents. Allow fetchDesigns
      // only when paginating (i.e. offset > 0, meaning the initial page is already in the cache).
      if (folderId !== undefined && (view?.designsOffset ?? 0) === 0) return false;
      return true;
    },
  },
);

interface FetchFolderContentsArg {
  viewKey: string;
  folderId: string;
  isShared?: boolean;
}

interface FetchFolderContentsResult {
  viewKey: string;
  folders: FolderData[];
  designs: DesignData[];
  more: boolean;
  nextDesignsOffset: number;
}

export const fetchFolderContents = createAsyncThunk<FetchFolderContentsResult, FetchFolderContentsArg, {state: RootState}>(
  'posterDesignPanel/fetchFolderContents',
  async ({viewKey, folderId, isShared}) => {
    const response = (await window.PMW.writeLocal('mydesigns/getFolderContents', {
      folderId,
      offset: 0,
      isShared: isShared ?? false,
    })) as {folders: FolderData[]; designs: DesignData[]; more: boolean};
    return {
      viewKey,
      folders: response.folders,
      designs: response.designs,
      more: response.more,
      nextDesignsOffset: response.designs.length,
    };
  },
  {
    condition: ({viewKey}, {getState}) => {
      const view = getState().posterDesignPanel.views[viewKey];
      if (view?.foldersLoading || view?.designsLoading) return false;
      return !(view?.foldersLoaded && view.designsLoaded);
    },
  },
);

interface FetchSearchResultsArg {
  searchKey: string;
  searchTerm: string;
  isShared: boolean;
}

interface FetchSearchResultsResult {
  searchKey: string;
  isFirstPage: boolean;
  folders: FolderData[];
  designs: DesignData[];
  more: boolean;
  nextOffset: number;
}

export const fetchSearchResults = createAsyncThunk<FetchSearchResultsResult, FetchSearchResultsArg, {state: RootState}>(
  'posterDesignPanel/fetchSearchResults',
  async ({searchKey, searchTerm, isShared}, {getState}) => {
    const offset = getState().posterDesignPanel.searchResults[searchKey]?.designsOffset ?? 0;
    const response = (await window.PMW.writeLocal('mydesigns/searchDesigns', {
      searchTerm,
      isShared: isShared ? 'true' : 'false',
      offset,
    })) as {folders: FolderData[]; designs: DesignData[]; more: boolean};
    return {
      searchKey,
      isFirstPage: offset === 0,
      folders: response.folders,
      designs: response.designs,
      more: response.more,
      nextOffset: offset + response.designs.length,
    };
  },
  {
    condition: ({searchKey}, {getState}) => {
      return !getState().posterDesignPanel.searchResults[searchKey]?.designsLoading;
    },
  },
);
